import {
    FULL_DAY_KEY,
    NOT_ALLOTMENT_FOR_NEXT_YEAR,
    SUBMIT_HR_APPROVAL,
    UNABLE_SUBMIT_MORE_THAN_ALLOTMENT,
    daysOptions,
} from "shared/constants/timeOff";
import {
    LoadingWrapper,
    OptionsWrapper,
    StyledTextArea,
    SupportWrapper,
} from "../custom-styles/new-edit-form/NewEditForm.styles";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { addYears, getMonth, getYear, isAfter, isBefore, isSameDay, lastDayOfYear, parseISO, startOfDay, startOfYear, subMonths } from "date-fns";
import {
    areDatesWithSameYear,
    calculateHalfDaySelection,
    checkIfHalfDaysOverlapsOK,
    checkIfHalfDaysOverlapsWrong,
    formDataToOOOData,
    getBusinessDaysCount,
    getDayOption,
    getDisabledTimeOffDates,
    getDropdownOptions,
    getOverlappingTimeOffs,
    parseDate,
    parseDatePickerDate,
    transformTimeOffDataToFormData,
} from "features/timesheet/time-off/timeOffFunctions";

import AllotmentInfo from "../custom-components/AllotmentInfo";
import { AllotmentTypes } from "shared/constants/timesheetEnums";
import ContactSupport from "../custom-components/ContactSupport";
import DateRangePicker from "shared/components/inputs/form-fields/DateRangePicker";
import DateRangevalidationForAllotment from "../custom-components/DateRangeValidationForAllotment";
import Dropdown from "shared/components/inputs/form-fields/Dropdown";
import FormComponent from "shared/components/inputs/form/FormComponent";
import HStack from "shared/components/layout/stack/HStack";
import Loading from "shared/components/feedback/loading/Loading";
import ModalActions from "shared/components/feedback/modals/ModalActions";
import ModalErrorMessage from "shared/components/feedback/modals/common/ModalErrorMessage";
import NoApproverAssinged from "../custom-components/NoApproverAssigned";
import Options from "shared/components/inputs/form-fields/Options";
import PropTypes from "prop-types";
import ReviewerCard from "../custom-components/ReviewerCard";
import SessionContext from "app/contexts/SessionContext";
import VStack from "shared/components/layout/stack/VStack";
import { sendNotification } from "shared/components/feedback/notification/notification";
import useAccrualRulesApi from "../querys/useAccrualRules";
import { useForm } from "react-hook-form";
import useOffices from "features/admin/offices/useOffices";
import useTimeOffApi from "../querys/useTimeOff";

const today = new Date();
const oneYearFromToday = addYears(today, 1);
const currentYear = getYear(today);
const nextYear = currentYear + 1;
const defaultDayOption = getDayOption(true, true);
const twoMonthsAgoDate = subMonths(today, 2);
const defaultValues = transformTimeOffDataToFormData(null);
const TYPES_WITHOUT_ALLOTMENT = new Set([AllotmentTypes.OTHERS, AllotmentTypes.GENERALREST]);

//this is used to disabled the navigation of the date picker to more than 2 months ago
const minPreviousDate = {
    year: getYear(twoMonthsAgoDate),
    month: getMonth(twoMonthsAgoDate) + 1, // date-fns use 0-11 for months
};

const NewTimeOffRequestForm = ({ onClose }) => {
    const session = useContext(SessionContext);
    const { approverId, legacyId: userId, currentOfficeId, type, agencyId } = session;
    const { useGetAccrualRulesByFilter } = useAccrualRulesApi();
    const { useGetOfficeById } = useOffices();
    const methods = useForm({ defaultValues, mode: "onChange" });
    const {
        useTimeOff,
        useOfficeHolidays,
        useGetTimeOffRequestsForForm,
        useGetTimeOffReasonsForNewRequest,
        useGetTimeOffApprover,
        createTimeOffRequest } = useTimeOffApi();

    const noApproverAssigned = !approverId || approverId <= 0;

    const userTimeOffReasons = useTimeOff({
        employeeId: userId,
        year: currentYear
    });

    const userTimeOffReasonsNextYear = useTimeOff({
        employeeId: userId,
        year: nextYear
    });

    const accrualRules = useGetAccrualRulesByFilter({
        officeId: currentOfficeId,
        employeeTypeId: type,
        year: currentYear
    });

    const holidays = useOfficeHolidays({
        officeId: currentOfficeId
    });

    const timeOffRequests = useGetTimeOffRequestsForForm({
        employeeId: userId
    });

    const userOffice = useGetOfficeById({
        officeId: currentOfficeId
    });

    const approver = useGetTimeOffApprover({
        employeeId: approverId
    });

    const reasons = useGetTimeOffReasonsForNewRequest({
        employeeId: userId
    });

    const reasonsDropdownOptions = useMemo(() => {
        const result = reasons.isLoading
            ? []
            : getDropdownOptions(reasons.data, "reasonId", "reasonName");

        return result;

    }, [reasons.isLoading, reasons.data]);

    const parsedHolidays = useMemo(() => {
        return holidays?.data?.map((x) => new Date(x.Date)) || [];
    }, [holidays?.data]);

    const nonRejectedTimeOffs = useMemo(() =>
        timeOffRequests?.data?.filter(x => !x.rejected) || [],
        [timeOffRequests?.data]);

    const disabledDaysForCalendar = useMemo(() => {
        const approvedTimeOffs = nonRejectedTimeOffs || [];
        const timeOffDays = getDisabledTimeOffDates(approvedTimeOffs, twoMonthsAgoDate);

        return [...timeOffDays, ...parsedHolidays];
    }, [nonRejectedTimeOffs, parsedHolidays]);

    const [hideSecondDayOptions, setHideSecondDayOptions] = useState(true);
    const [disabledSubmit, setDisabledSubmit] = useState(false);
    const [startOOODate, setStartOOODate] = useState();
    const [endOOODate, setEndOOODate] = useState();
    const [enableDaysFromDate, setEnableDaysFromDate] = useState(today);
    const [enableDaysUntilDate, setEnableDaysUntilDate] = useState(oneYearFromToday);
    const [exceededDaysErrorMessage, setExceededDaysErrorMessage] = useState("");
    const [isPTORequest, setIsPTORequest] = useState(false);
    const [showAllowed, setShowAllowed] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [reasonType, setReasonType] = useState(null);
    const [firstDaySelection, setFirstDaySelection] = useState(defaultDayOption);
    const [secondDaySelection, setSecondDaySelection] = useState(defaultDayOption);
    const [showAllotmentNextYear, setShowAllotmentNextYear] = useState(false);
    const [nextYearAllotment, setNextYearAllotment] = useState(null);
    const [yearOfRequest, setYearOfRequest] = useState(currentYear);

    const calculateAmountOfDaysOut = useCallback((startDate, endDate, firstDayOption, secondDayOption) => {

        const firstDay = parseDate(startDate);
        const lastDay = parseDate(endDate);

        const bussinessDates = getBusinessDaysCount(firstDay, lastDay, parsedHolidays);

        const halfDaySelection = calculateHalfDaySelection(firstDay, lastDay, firstDayOption, secondDayOption);

        const result = bussinessDates - halfDaySelection;
        return result;
    }, [parsedHolidays]);

    const handleExceededDays = useCallback((reason) => {
        const isPTO = reason.allotmentTypeId === AllotmentTypes.PTO;
        const disableSubmit = !isPTO;
        let message = reason.allotment == null
            ? NOT_ALLOTMENT_FOR_NEXT_YEAR
            : UNABLE_SUBMIT_MORE_THAN_ALLOTMENT;

        if (isPTO) {
            message = SUBMIT_HR_APPROVAL;
        }

        setExceededDaysErrorMessage(message);
        setDisabledSubmit(disableSubmit);
        setIsPTORequest(isPTO);
    }, []);

    const checkExceededDays = useCallback((reason, startDate, endDate, firstDayOption, lastDayOption) => {
        setExceededDaysErrorMessage("");

        if (!startDate || !endDate || !reason) {
            return;
        }

        const firstDayParsed = parseDate(startDate);
        const lastDayParsed = parseDate(endDate);

        const amountOfDaysOut = calculateAmountOfDaysOut(firstDayParsed, lastDayParsed, firstDayOption, lastDayOption);
        const firstDayYear = getYear(firstDayParsed);
        const lastDayYear = getYear(lastDayParsed);

        const reasonNextYear = userTimeOffReasonsNextYear.data?.find(x =>
            x.reasonId === reason.reasonId && x.allotment != null);

        const isPTO = reason.allotmentTypeId === AllotmentTypes.PTO;

        const calculateInEachYear = isPTO || (!isPTO && !!reasonNextYear);

        // handle multiyear
        if (firstDayYear !== lastDayYear && calculateInEachYear) {

            // check for exceeded for current year request
            const lastDayOfYearDate = lastDayOfYear(firstDayParsed);

            const amountOfDaysOutCurrentYear = calculateAmountOfDaysOut(startDate, lastDayOfYearDate, firstDayOption, "FullDay");

            if (amountOfDaysOutCurrentYear > reason.daysRemaining) {
                handleExceededDays(reason);
                return;
            }

            // check for exceeded for next year request
            const firstDayOfYearDate = startOfYear(lastDayParsed);
            const amountOfDaysOutNextYear = calculateAmountOfDaysOut(firstDayOfYearDate, endDate, "FullDay", lastDayOption);

            if (!reasonNextYear || amountOfDaysOutNextYear > reasonNextYear.daysRemaining) {
                handleExceededDays(reason);
                return;
            }

            setDisabledSubmit(false);
        }
        else if (amountOfDaysOut > reason.daysRemaining) {
            handleExceededDays(reason);
        } else {
            setDisabledSubmit(false);
        }
    }, [calculateAmountOfDaysOut, handleExceededDays, userTimeOffReasonsNextYear.data]);

    // actually maybe setReason?
    const getReason = useCallback((val) => {
        let isNextYear = false;
        let isMultiYear = false;
        let reasonsByYear = userTimeOffReasons?.data;
        const startDateValue = methods.getValues("date").start;
        const endDateValue = methods.getValues("date").end;
        const startDate = parseDate(startDateValue)
        const endDate = parseDate(endDateValue)

        // check if user request a "next year request" or a "multiyear request"
        if (startDate) {
            const firstDayYear = getYear(startDate);
            isNextYear = firstDayYear !== currentYear;
            setYearOfRequest(firstDayYear);

            // try to find reason and allotment for next year
            if (isNextYear && userTimeOffReasonsNextYear?.data?.length > 0) {
                reasonsByYear = userTimeOffReasonsNextYear?.data;
            }
            // is multi year?
            else if (endDate) {
                isMultiYear = !areDatesWithSameYear(startDate, endDate);

                if (isMultiYear) {
                    let reasonMultiyear = null;
                    // try to find reason and allotment for next year
                    if (userTimeOffReasonsNextYear?.data?.length > 0) {
                        reasonMultiyear = userTimeOffReasonsNextYear.data.find((x) => x.reasonId === val);
                    }
                    reasonMultiyear = reasonMultiyear ?? { allotment: 0, daysRemaining: 0 };
                    setNextYearAllotment(reasonMultiyear);
                }
            }
        }

        setShowAllotmentNextYear(isMultiYear);

        let reason = reasonsByYear?.find((x) => x.reasonId === val)
            ?? reasons?.data?.find((x) => x.reasonId === val)
            ?? { reasonId: val };

        setReasonType(reason);
        return reason;
    }, [userTimeOffReasons?.data, userTimeOffReasonsNextYear?.data, reasons?.data, methods]);

    const dateRangeChange = useCallback((dates) => {
        const start = dates.start;
        const end = dates.end;
        setStartOOODate(start);
        setEndOOODate(end);

        handleShowHideSecondDayOptions(start, end);

        if (showAllowed) {
            const reason = reasonType ? getReason(reasonType.reasonId) : null;
            checkExceededDays(reason, start, end, firstDaySelection, secondDaySelection);
        }
    }, [showAllowed, reasonType, firstDaySelection, secondDaySelection, getReason, checkExceededDays]);

    const handleShowHideSecondDayOptions = (start, end) => {
        const startDate = parseDatePickerDate(start);
        const endDate = parseDatePickerDate(end);

        const sameDay = startDate && endDate && isSameDay(startDate, endDate);
        setHideSecondDayOptions(sameDay);
    };

    const reasonDropDownChange = (val) => {
        if ((reasonType?.reasonId ?? "") === val) {
            return;
        }

        const reason = getReason(val);
        methods.clearErrors("date");
        setCalendarAccrualRules(reason);

        const reasonWithoutAllotment = !reason.allotmentTypeId
            || TYPES_WITHOUT_ALLOTMENT.has(reason.allotmentTypeId);

        if (reasonWithoutAllotment) {
            noLimitationDays();
        } else {
            setShowAllowed(true);
            checkExceededDays(reason, startOOODate, endOOODate, firstDaySelection, secondDaySelection);
        }
    };

    const noLimitationDays = () => {
        setShowAllowed(false);
        clearExceededDaysErrorMessage();
    }

    const clearExceededDaysErrorMessage = () => {
        setDisabledSubmit(false);
        setExceededDaysErrorMessage("");
    }

    const setCalendarAccrualRules = (reason) => {
        if (!accrualRules?.data) {
            return;
        };

        let dateFrom = null;
        let dateUntil = null;

        switch (reason?.allotmentTypeId) {
            case AllotmentTypes.CARR: // carry over days
                if (accrualRules?.data?.lastDayForCarryOver) {
                    dateUntil = parseISO(accrualRules?.data?.lastDayForCarryOver);
                }
                break;
            case AllotmentTypes.SMRNY: // summer days
                if (accrualRules?.data?.summerDaysStart) {
                    dateFrom = parseISO(accrualRules?.data?.summerDaysFirst);
                }
                if (accrualRules?.data?.summerDaysEnd) {
                    dateUntil = parseISO(accrualRules?.data?.summerDaysEnd);
                }
                break;
            case AllotmentTypes.PTOAUTO: // carry over sick days
                if (accrualRules?.data?.lastDayForCarryOverSick) {
                    dateUntil = parseISO(accrualRules?.data?.lastDayForCarryOverSick);
                }
                break;
            default:
                dateFrom = today;
                break;
        }

        if (!dateFrom || isBefore(dateFrom, today)) {
            dateFrom = today;
        }

        setEnableDaysFromDate(dateFrom);
        setEnableDaysUntilDate(dateUntil);
    };

    const onSubmit = (formData) => {

        setIsSubmitting(true);

        const outOfOfficeData = formDataToOOOData(
            userId,
            approverId,
            0,
            reasonsDropdownOptions,
            agencyId,
            formData
        );

        outOfOfficeData.AmountOfDaysOut = calculateAmountOfDaysOut(
            outOfOfficeData.firstDayOut,
            outOfOfficeData.lastDayOut,
            outOfOfficeData.firstDayOptions,
            outOfOfficeData.lastDayOptions
        );

        if (outOfOfficeData.AmountOfDaysOut <= 0) {
            sendNotification(`No valid dates - check holidays`);
            setIsSubmitting(false);
            return;
        }

        sendRequest(outOfOfficeData);
    };

    const dateRangeValidations = (value) => {
        const firstDayOut = parseDatePickerDate(value?.start);
        const lastDayOut = parseDatePickerDate(value?.end);

        if (!firstDayOut || !lastDayOut) {
            return "Dates are not set";
        }

        const cleanfirstDayOut = startOfDay(firstDayOut);
        const cleanLastDayOut = startOfDay(lastDayOut);
        const cleanEnableDaysFromDate = startOfDay(enableDaysFromDate);
        const cleanEnableDaysUntilDate = startOfDay(enableDaysUntilDate);

        // min date validation - basic is tomorrow - also there might be accrual rules
        if (isBefore(cleanfirstDayOut, cleanEnableDaysFromDate)) {
            return "Min date not valid";
        }

        // max date validation - 1 year from today - also there might be accrual rules
        if (isAfter(cleanLastDayOut, cleanEnableDaysUntilDate)) {
            return "Max date not valid";
        }

        const overlapsError = datesOverlapsWithExisting(cleanfirstDayOut, cleanLastDayOut);
        if (overlapsError) {
            return overlapsError;
        }

        const overlapedDate = disabledDaysForCalendar.some((x) => isSameDay(x, firstDayOut) || isSameDay(x, lastDayOut));
        if (overlapedDate) {
            return "Dates are not allowed - Check your existing holidays or existing time off";
        }

        return undefined;
    };

    const datesOverlapsWithExisting = (startDate, endDate) => {
        const firstDayOption = methods.getValues("firstDayOptions");
        let lastDayOption = null;

        if (!isSameDay(startDate, endDate)) {
            const option = methods.getValues("lastDayOptions");
            lastDayOption = option ?? FULL_DAY_KEY;
        }

        const overlaps = getOverlappingTimeOffs(nonRejectedTimeOffs, startDate, endDate);

        const halfDaysOverlapsOk = checkIfHalfDaysOverlapsOK(overlaps, startDate, endDate, firstDayOption, lastDayOption);

        if (halfDaysOverlapsOk.length >= overlaps.length) {
            return "";
        }

        const halfDaysOverlapsWrong = checkIfHalfDaysOverlapsWrong(overlaps, startDate, endDate, firstDayOption, lastDayOption);

        if (halfDaysOverlapsWrong) {
            return "Half day overlaps with existing time off";
        }

        return (overlaps?.length > 0) ? "Dates overlaps with existing time off" : "";
    };

    const changeFirstDay = async (selection) => {
        setFirstDaySelection(selection);
        if (showAllowed) {
            checkExceededDays(reasonType, startOOODate, endOOODate, selection, secondDaySelection);
        }
        // this will trigger the validation of the control named "date"
        await methods.trigger("date");
    };

    const changeLastDay = async (selection) => {
        setSecondDaySelection(selection);
        if (showAllowed) {
            checkExceededDays(reasonType, startOOODate, endOOODate, firstDaySelection, selection);
        }
        // this will trigger the validation of the control named "date"
        await methods.trigger("date");
    };

    const closeForm = () => {
        onClose();
        setIsSubmitting(false);
    };

    const sendRequest = async (outOfOfficeData) => {
        await createTimeOffRequest.mutateAsync(outOfOfficeData);
        sendNotification(`Time off request has been submitted`);
        closeForm();
    };

    const submitAction = isSubmitting
        ? { isSubmiting: true }
        : {
            type: "submit",
            label: "Submit",
            disabled: !approver?.data,
        };

    const submitActionDisabled = {
        type: "submit",
        label: "Submit",
        disabled: true,
    };

    const secondaryAction = {
        type: "button",
        label: "Close",
        onClick: onClose,
        fullWidth: noApproverAssigned,
    };

    const primaryActionButton = isSubmitting
        ? { isSubmiting: true }
        : {
            primaryAction: disabledSubmit
                ? submitActionDisabled
                : submitAction,
        };

    const isLoading = timeOffRequests.isLoading
        || userTimeOffReasons.isLoading
        || userTimeOffReasonsNextYear.isLoading
        || reasons.isLoading
        || accrualRules.isLoading;

    const hasError = timeOffRequests.error
        || reasons.error
        || userTimeOffReasons.error
        || accrualRules.error;

    if (isLoading) {
        return (
            <LoadingWrapper>
                <Loading text="Loading form..." />
            </LoadingWrapper>
        );
    }

    if (hasError) {
        return <ModalErrorMessage />;
    }

    if (isSubmitting) {
        return (
            <LoadingWrapper>
                <Loading text="Submitting..." />
            </LoadingWrapper>
        );
    }

    return (
        <FormComponent
            methods={methods}
            onSubmit={onSubmit}
            defaultValues={defaultValues}
        >
            <VStack>
                <VStack spacing="Two">
                    {noApproverAssigned &&
                        <VStack spacing="Two">
                            <NoApproverAssinged />
                            {!userOffice.isLoading && (
                                <ContactSupport userOffice={userOffice} />
                            )}
                        </VStack>
                    }
                    <VStack spacing="One">
                        <Dropdown
                            name="OOOType"
                            validations={{ required: true, }}
                            onChange={reasonDropDownChange}
                            label="Type Of Request"
                            options={reasonsDropdownOptions}
                            disabled={noApproverAssigned}
                        />
                        {showAllowed && reasonType != null && (
                            <AllotmentInfo
                                reasonType={reasonType}
                                showAllotmentNextYear={showAllotmentNextYear}
                                nextYearAllotment={nextYearAllotment}
                                yearOfRequest={yearOfRequest}
                            />
                        )}
                    </VStack>
                    <VStack spacing="Zero_5">
                        <DateRangePicker
                            name="date"
                            label="Date"
                            onChange={dateRangeChange}
                            validations={{
                                required: true,
                                validate: dateRangeValidations,
                            }}
                            disableDays={disabledDaysForCalendar}
                            minDate={minPreviousDate}
                            enableDaysFromDate={enableDaysFromDate}
                            enableDaysUntilDate={enableDaysUntilDate}
                            disabled={noApproverAssigned}
                        />
                        {exceededDaysErrorMessage !== "" && (
                            <DateRangevalidationForAllotment
                                isPTORequest={isPTORequest}
                                exceededDaysErrorMessage={
                                    exceededDaysErrorMessage
                                }
                            />
                        )}
                    </VStack>
                    <OptionsWrapper>
                        <HStack spacing="Seven">
                            <Options
                                name="firstDayOptions"
                                label={"Start Date"}
                                multiselect={false}
                                options={daysOptions}
                                defaultSelection={"FullDay"}
                                onValueSelected={changeFirstDay}
                                disabled={noApproverAssigned}
                            />
                            {!hideSecondDayOptions && (
                                <Options
                                    name="lastDayOptions"
                                    label={"End Date"}
                                    multiselect={false}
                                    options={daysOptions}
                                    defaultSelection={"FullDay"}
                                    onValueSelected={changeLastDay}
                                    disabled={noApproverAssigned}
                                />
                            )}
                        </HStack>
                    </OptionsWrapper>
                    <StyledTextArea
                        name="note"
                        showClearIcon={true}
                        label="Provide further information"
                        description="This field is optional"
                        validations={{
                            maxLength: {
                                value: 120,
                                message: "Please use fewer characters",
                            },
                        }}
                        disabled={noApproverAssigned}
                    />
                    {!noApproverAssigned && !approver.isLoading && (
                        <ReviewerCard approver={approver} />
                    )}
                    {!noApproverAssigned && !userOffice.isLoading && (
                        <SupportWrapper spacing="One">
                            <ContactSupport userOffice={userOffice} />
                        </SupportWrapper>
                    )}
                </VStack>

                <ModalActions
                    //Primary Action Button
                    {...primaryActionButton}
                    //Secondary Action Button - Close button
                    secondaryAction={secondaryAction}
                    as="fieldset"
                />
            </VStack>
        </FormComponent>
    );
};

export default NewTimeOffRequestForm;

NewTimeOffRequestForm.propTypes = {
    onClose: PropTypes.func.isRequired
};